import type { RootState } from '@/app/store/store';
import type { PhysicalStore } from '../../domain/models/PhysicalStore';
import type { PhysicalStoresDetails } from '../../domain/models/PhysicalStoresDetails';
import type { StoreCityGroup } from '../../domain/models/StoreCityGroup';
import type { PhysicalStoreRepository } from '../../domain/interfaces/PhysicalStoreRepository';
import type { PhysicalStoreQueryPort } from '../interfaces/PhysicalStoreQueryPort';
import type { StoreLocatorData } from '../../domain/models/StoreLocatorData';
import type { StoreLocationWidgetData } from '../../domain/models/StoreLocationWidgetData';

export type PhysicalStoreService = ReturnType<typeof createPhysicalStoreService>;

export function createPhysicalStoreService(repo: PhysicalStoreRepository & PhysicalStoreQueryPort) {
  return {
    async getStoreLocationWidget(): Promise<StoreLocationWidgetData | null> {
      return repo.fetchStoreLocationWidget();
    },
    invalidateStoreLocationWidget(): void {
      repo.invalidateStoreLocationWidget();
    },
    prefetchStoresDetails(): void {
      repo.prefetchStoresDetails();
    },
    selectStoresDetails(state: RootState): PhysicalStoresDetails | undefined {
      return repo.selectStoresDetails(state);
    },
    prefetchStoreLocator(): void {
      repo.prefetchStoreLocator();
    },
    selectStoreLocator(state: RootState): StoreLocatorData | undefined {
      return repo.selectStoreLocator(state);
    },
    getSortedCityGroups(
      cityToStoresMap: Record<string, PhysicalStore[]> | undefined
    ): StoreCityGroup[] {
      if (!cityToStoresMap) return [];
      return Object.keys(cityToStoresMap)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b))
        .map((cityName) => ({
          cityName,
          stores: cityToStoresMap[cityName] ?? [],
        }));
    },
    getStoresForCity(
      cityToStoresMap: Record<string, PhysicalStore[]> | undefined,
      cityName: string
    ): PhysicalStore[] {
      if (!cityToStoresMap || !cityName) return [];
      const normalized = cityName.trim().toLowerCase();
      for (const key of Object.keys(cityToStoresMap)) {
        if (key.trim().toLowerCase() === normalized) {
          return cityToStoresMap[key] ?? [];
        }
      }
      return [];
    },
    filterCityGroups(
      groups: StoreCityGroup[],
      selectedCity: string | 'all'
    ): StoreCityGroup[] {
      if (selectedCity === 'all') return groups;
      return groups.filter(
        (group) => group.cityName.trim().toLowerCase() === selectedCity.trim().toLowerCase()
      );
    },
  };
}
